/** Tab-local working state, never evidence that an action has completed. */
import { useCallback, useState } from "react";
import { API_BASE } from "./base";

const LIMIT = 128 * 1024;
export const draftKey = (name: string, scope = API_BASE || "/") => `pa:working:${scope}:${name}`;

export function readDraft<T>(name: string, fallback: T, scope?: string): T {
  try {
    const raw = sessionStorage.getItem(draftKey(name, scope));
    return raw === null ? fallback : JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

/** Forget one draft. Clearing is not writing `null`: nothing is left behind. */
export function clearDraft(name: string, scope?: string): void {
  try { sessionStorage.removeItem(draftKey(name, scope)); } catch { /* a tab that keeps nothing keeps no draft either */ }
}

export function writeDraft(name: string, value: unknown, scope?: string): void {
  const raw = JSON.stringify(value);
  if (raw.length > LIMIT) throw new Error("这份界面草稿过大，未能保存在当前标签页。");
  sessionStorage.setItem(draftKey(name, scope), raw);
}

export function useDraft<T>(name: string, initial: T, scope?: string) {
  const [stored, setValue] = useState(() => ({ name, scope, value: readDraft(name, initial, scope) }));
  const value = stored.name === name && stored.scope === scope ? stored.value : readDraft(name, initial, scope);
  const [error, setError] = useState<string | null>(null);
  const update = useCallback((next: T) => {
    setValue({ name, scope, value: next });
    try { writeDraft(name, next, scope); setError(null); }
    catch { setError("草稿暂时无法保存，请不要关闭这一页。"); }
  }, [name, scope]);
  return [value, update, error] as const;
}
